import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Filter, LogOut, Mail, Search, Shield, Ticket, Users } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CONTACT_EMAIL } from "@/content/siteContent";
import { usePageSEO } from "@/hooks/usePageSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "fanta-royal-admin-token";

const initialLoginForm = {
  email: "",
  password: "",
};

const initialFilters = {
  query: "",
  date_from: "",
  date_to: "",
  payment_status: "all",
};

const formatDateTime = (value) => {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat("it-IT", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const formatAmount = (amount, currency = "EUR") => {
  if (amount === null || amount === undefined || amount === "") return "—";

  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch (error) {
    return `${amount} ${currency}`;
  }
};

export default function AdminDashboardPage() {
  usePageSEO({
    title: "Admin Dashboard",
    description: "Area admin Fanta Royal per verificare lead, pagamenti ticket, filtri ed export CSV.",
    canonicalPath: "/admin",
    noindex: true,
  });

  const [token, setToken] = useState(() => 
    // Using sessionStorage instead of localStorage for security - token cleared on browser close
    window.sessionStorage.getItem(ADMIN_TOKEN_KEY) || ""
  );
  const [loginForm, setLoginForm] = useState(initialLoginForm);
  const [loginState, setLoginState] = useState({ status: "idle", message: "" });
  const [activeTab, setActiveTab] = useState("leads");
  const [filters, setFilters] = useState(initialFilters);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ leads: 0, payments: 0 });
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const persistToken = (value) => {
    if (value) {
      // Using sessionStorage for better security - token auto-expires on browser close
      window.sessionStorage.setItem(ADMIN_TOKEN_KEY, value);
      setToken(value);
      return;
    }

    window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();

    if (filters.query.trim()) params.set("query", filters.query.trim());
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (activeTab === "payments" && filters.payment_status && filters.payment_status !== "all") {
      params.set("payment_status", filters.payment_status);
    }
    params.set("limit", "100");

    return params.toString();
  };

  const handleUnauthorized = () => {
    persistToken("");
    setSummary(null);
    setLeads([]);
    setPayments([]);
    setTotals({ leads: 0, payments: 0 });
    setPageError("Sessione admin scaduta. Effettua di nuovo il login.");
  };

  const fetchSummary = async (headers = authHeaders) => {
    const response = await axios.get(`${API}/admin/summary`, { headers });
    setSummary(response.data);
  };

  const fetchLeads = async (headers = authHeaders) => {
    const params = buildQueryParams();
    const response = await axios.get(`${API}/admin/leads${params ? `?${params}` : ""}`, { headers });
    setLeads(response.data.items);
    setTotals((current) => ({ ...current, leads: response.data.total }));
  };

  const fetchPayments = async (headers = authHeaders) => {
    const params = buildQueryParams();
    const response = await axios.get(`${API}/admin/payments${params ? `?${params}` : ""}`, { headers });
    setPayments(response.data.items);
    setTotals((current) => ({ ...current, payments: response.data.total }));
  };

  const loadDashboard = async (headers = authHeaders) => {
    setLoading(true);
    setPageError("");

    try {
      await fetchSummary(headers);
      await Promise.all([fetchLeads(headers), fetchPayments(headers)]);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        setPageError(error.response?.data?.detail || "Non siamo riusciti a caricare la dashboard admin.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginState({ status: "loading", message: "Verifica credenziali in corso…" });

    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      persistToken(response.data.token);
      setLoginState({ status: "success", message: response.data.message });
      setLoginForm(initialLoginForm);
    } catch (error) {
      setLoginState({
        status: "error",
        message: error.response?.data?.detail || "Login admin non riuscito.",
      });
    }
  };

  const handleApplyFilters = async () => {
    if (!token) return;
    setLoading(true);
    setPageError("");

    try {
      if (activeTab === "leads") {
        await fetchLeads();
      } else {
        await fetchPayments();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        setPageError(error.response?.data?.detail || "Filtri non applicati correttamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const params = buildQueryParams();
      const url = `${API}/admin/${type}/export.csv${params ? `?${params}` : ""}`;
      const response = await axios.get(url, {
        headers: authHeaders,
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", type === "leads" ? "fanta-royal-leads.csv" : "fanta-royal-payments.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        setPageError(error.response?.data?.detail || "Export CSV non disponibile.");
      }
    }
  };

  const handleLogout = () => {
    persistToken("");
    setLoginState({ status: "idle", message: "" });
    setPageError("");
  };

  if (!token) {
    return (
      <div className="status-page-shell page-noise admin-page-shell">
        <div className="status-page-inner admin-login-shell">
          <Link to="/" className="brand-lockup" data-testid="admin-home-link">
            <span className="brand-mark" aria-hidden="true">
              <Shield className="h-4 w-4" />
            </span>
            <div>
              <span className="wordmark">Fanta Royal</span>
              <p className="brand-tagline">Admin dashboard</p>
            </div>
          </Link>

          <Card className="luxury-card admin-login-card" data-testid="admin-login-card">
            <CardContent className="p-6 sm:p-8">
              <span className="section-kicker">Accesso admin</span>
              <h1 className="section-title status-title">Verifica iscritti, lead e pagamenti</h1>
              <p className="section-description status-copy">
                Area riservata per consultare i dati utili alla creazione delle leghe, ai contatti e alla verifica dei ticket acquistati.
              </p>

              <form className="admin-login-form" onSubmit={handleLoginSubmit} data-testid="admin-login-form">
                <div className="space-y-2">
                  <Label htmlFor="admin_email">Email admin</Label>
                  <Input
                    id="admin_email"
                    name="email"
                    type="email"
                    value={loginForm.email}
                    onChange={handleLoginChange}
                    className="form-input"
                    placeholder="admin@dominio.it"
                    data-testid="admin-login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_password">Password</Label>
                  <Input
                    id="admin_password"
                    name="password"
                    type="password"
                    value={loginForm.password}
                    onChange={handleLoginChange}
                    className="form-input"
                    placeholder="Inserisci la password"
                    data-testid="admin-login-password-input"
                  />
                </div>

                <Button className="primary-cta admin-login-submit" type="submit" data-testid="admin-login-submit-button">
                  {loginState.status === "loading" ? "Accesso in corso…" : "Accedi al pannello"}
                </Button>

                {loginState.status === "error" ? (
                  <p className="form-error-block" data-testid="admin-login-error-message">
                    {loginState.message}
                  </p>
                ) : null}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="status-page-shell page-noise admin-page-shell">
      <div className="admin-dashboard-shell">
        <header className="admin-dashboard-header">
          <div>
            <span className="section-kicker">Dashboard unica</span>
            <h1 className="section-title admin-page-title">Controllo iscritti, contatti e ticket</h1>
            <p className="section-description admin-page-copy">
              Visualizza lead, acquisti ticket e dati utili alla gestione della stagione direttamente da un’unica area protetta.
            </p>
          </div>

          <div className="admin-header-actions">
            <Button asChild variant="outline" className="secondary-cta">
              <Link to="/" data-testid="admin-back-home-button">Torna al sito</Link>
            </Button>
            <Button className="primary-cta" onClick={handleLogout} type="button" data-testid="admin-logout-button">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        <section className="admin-summary-grid" data-testid="admin-summary-grid">
          <Card className="luxury-card admin-summary-card">
            <CardContent className="p-6">
              <div className="admin-summary-icon"><Users className="h-5 w-5" /></div>
              <p className="admin-summary-label">Lead raccolti</p>
              <p className="admin-summary-value">{summary?.total_leads ?? 0}</p>
              <p className="admin-summary-meta">Ultimo lead: {formatDateTime(summary?.latest_lead_at)}</p>
            </CardContent>
          </Card>

          <Card className="luxury-card admin-summary-card">
            <CardContent className="p-6">
              <div className="admin-summary-icon"><Ticket className="h-5 w-5" /></div>
              <p className="admin-summary-label">Ordini ticket</p>
              <p className="admin-summary-value">{summary?.total_payments ?? 0}</p>
              <p className="admin-summary-meta">Ultimo ordine: {formatDateTime(summary?.latest_payment_at)}</p>
            </CardContent>
          </Card>

          <Card className="luxury-card admin-summary-card">
            <CardContent className="p-6">
              <div className="admin-summary-icon"><Shield className="h-5 w-5" /></div>
              <p className="admin-summary-label">Pagamenti confermati</p>
              <p className="admin-summary-value">{summary?.paid_payments ?? 0}</p>
              <p className="admin-summary-meta">In attesa: {summary?.pending_payments ?? 0}</p>
            </CardContent>
          </Card>

          <Card className="luxury-card admin-summary-card">
            <CardContent className="p-6">
              <div className="admin-summary-icon"><Mail className="h-5 w-5" /></div>
              <p className="admin-summary-label">Incasso stimato</p>
              <p className="admin-summary-value">{formatAmount(summary?.total_revenue_eur ?? 0)}</p>
              <p className="admin-summary-meta">Contatto ufficiale: {CONTACT_EMAIL}</p>
            </CardContent>
          </Card>
        </section>

        <Card className="luxury-card admin-controls-card">
          <CardContent className="p-6 sm:p-8">
            <div className="admin-tab-row">
              <button
                type="button"
                className={`admin-tab-button ${activeTab === "leads" ? "is-active" : ""}`}
                onClick={() => setActiveTab("leads")}
                data-testid="admin-tab-leads-button"
              >
                Lead
              </button>
              <button
                type="button"
                className={`admin-tab-button ${activeTab === "payments" ? "is-active" : ""}`}
                onClick={() => setActiveTab("payments")}
                data-testid="admin-tab-payments-button"
              >
                Pagamenti
              </button>
            </div>

            <div className="admin-filters-grid">
              <div className="space-y-2">
                <Label htmlFor="admin_search">Ricerca</Label>
                <div className="admin-filter-input-wrap">
                  <Search className="admin-filter-icon h-4 w-4" />
                  <Input
                    id="admin_search"
                    name="query"
                    value={filters.query}
                    onChange={handleFilterChange}
                    className="form-input admin-filter-input"
                    placeholder={activeTab === "leads" ? "Nome, email o telefono" : "Nome, email, telefono o sessione"}
                    data-testid="admin-filter-search-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_date_from">Dal</Label>
                <Input
                  id="admin_date_from"
                  name="date_from"
                  type="date"
                  value={filters.date_from}
                  onChange={handleFilterChange}
                  className="form-input"
                  data-testid="admin-filter-date-from-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_date_to">Al</Label>
                <Input
                  id="admin_date_to"
                  name="date_to"
                  type="date"
                  value={filters.date_to}
                  onChange={handleFilterChange}
                  className="form-input"
                  data-testid="admin-filter-date-to-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_status">Stato pagamento</Label>
                <select
                  id="admin_status"
                  name="payment_status"
                  value={filters.payment_status}
                  onChange={handleFilterChange}
                  className="admin-select"
                  disabled={activeTab !== "payments"}
                  data-testid="admin-filter-payment-status-select"
                >
                  <option value="all">Tutti</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="processing_confirmation">Processing</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>

            <div className="admin-actions-row">
              <Button className="primary-cta" type="button" onClick={handleApplyFilters} data-testid="admin-apply-filters-button">
                <Filter className="mr-2 h-4 w-4" />
                Applica filtri
              </Button>
              <Button
                variant="outline"
                className="secondary-cta"
                type="button"
                onClick={() => handleExport(activeTab)}
                data-testid="admin-export-csv-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV {activeTab === "leads" ? "Lead" : "Pagamenti"}
              </Button>
            </div>

            {pageError ? (
              <p className="form-error-block admin-page-error" data-testid="admin-page-error-message">
                {pageError}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card className="luxury-card admin-results-card">
          <CardContent className="p-6 sm:p-8">
            <div className="admin-results-header">
              <div>
                <span className="section-kicker">{activeTab === "leads" ? "Lead" : "Pagamenti"}</span>
                <h2 className="admin-results-title">
                  {activeTab === "leads" ? "Contatti ricevuti" : "Ticket e ordini confermati / in corso"}
                </h2>
              </div>
              <p className="admin-results-total" data-testid="admin-results-total">
                Totale: {activeTab === "leads" ? totals.leads : totals.payments}
              </p>
            </div>

            {loading ? <p className="admin-loading-state">Aggiornamento dati in corso…</p> : null}

            {activeTab === "leads" ? (
              <>
                <div className="admin-table-wrap hidden md:block">
                  <table className="admin-table" data-testid="admin-leads-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Telefono</th>
                        <th>Messaggio</th>
                        <th>Creato il</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((item) => (
                        <tr key={item.id}>
                          <td>{item.first_name} {item.last_name}</td>
                          <td>{item.email}</td>
                          <td>{item.phone || "—"}</td>
                          <td>{item.message || "—"}</td>
                          <td>{formatDateTime(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="admin-mobile-list md:hidden">
                  {leads.map((item) => (
                    <article key={item.id} className="admin-mobile-card" data-testid="admin-lead-mobile-card">
                      <p className="admin-mobile-name">{item.first_name} {item.last_name}</p>
                      <p>{item.email}</p>
                      <p>{item.phone || "—"}</p>
                      <p>{item.message || "—"}</p>
                      <p className="admin-mobile-date">{formatDateTime(item.created_at)}</p>
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="admin-table-wrap hidden md:block">
                  <table className="admin-table" data-testid="admin-payments-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Importo</th>
                        <th>Stato</th>
                        <th>Accepted at</th>
                        <th>Creato il</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((item) => (
                        <tr key={item.id}>
                          <td>{item.full_name}</td>
                          <td>{item.email}</td>
                          <td>{formatAmount(item.amount_paid ?? item.amount_due, item.currency)}</td>
                          <td>
                            <span className={`admin-status-badge admin-status-badge--${item.status}`}>
                              {item.status}
                            </span>
                          </td>
                          <td>{formatDateTime(item.accepted_at)}</td>
                          <td>{formatDateTime(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="admin-mobile-list md:hidden">
                  {payments.map((item) => (
                    <article key={item.id} className="admin-mobile-card" data-testid="admin-payment-mobile-card">
                      <p className="admin-mobile-name">{item.full_name}</p>
                      <p>{item.email}</p>
                      <p>{formatAmount(item.amount_paid ?? item.amount_due, item.currency)}</p>
                      <p className={`admin-status-badge admin-status-badge--${item.status}`}>{item.status}</p>
                      <p>Accepted: {formatDateTime(item.accepted_at)}</p>
                      <p className="admin-mobile-date">{formatDateTime(item.created_at)}</p>
                    </article>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
